import { borderRadius, colors, sxPresets } from '@/theme'
import { ExpandMore } from '@mui/icons-material'
import { Box, Collapse, IconButton, Paper, Typography } from '@mui/material'
import { useState } from 'react'

interface CollapsibleCardProps {
  sectionId: string
  title: string
  children: React.ReactNode
  defaultExpanded?: boolean
  emptyMessage?: string
  emptyContent?: React.ReactNode
  isEmpty?: boolean
  /** Optional actions rendered in the header, left of the expand button. */
  actions?: React.ReactNode
}

export default function CollapsibleCard({
  sectionId,
  title,
  children,
  defaultExpanded = true,
  emptyMessage,
  emptyContent,
  isEmpty = false,
  actions
}: CollapsibleCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const renderContent = () => {
    if (!isEmpty) return children

    if (emptyContent) return emptyContent

    if (emptyMessage) {
      return (
        <Box sx={{ ...sxPresets().section.content, p: 4, textAlign: 'center' }}>
          <Typography variant='body2'>{emptyMessage}</Typography>
        </Box>
      )
    }

    return children
  }

  return (
    <Paper
      id={sectionId}
      elevation={1}
      sx={{ border: `1px solid ${colors.border.default}`, borderRadius: borderRadius.lg, overflow: 'hidden' }}
    >
      <Box sx={sxPresets().section.header}>
        <Typography variant='sectionTitle'>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {actions}
          <IconButton
            size='small'
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ ...sxPresets().expandButton, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isExpanded}>{renderContent()}</Collapse>
    </Paper>
  )
}
